import React, { Component } from 'react';

import axios from 'axios';
import PropTypes from 'prop-types';

import { createReactRenderer } from 'helpers/react-renderer';
import Autocomplete from 'pages/components/Autocomplete';
import { runOnChangeCallbackFunc } from 'pages/containers/VendorsAutocomplete/utils';
import { submitErrors } from 'pages/utils/errors';
import sortCaseInsensitive from 'pages/utils/sort';

export default class BrandsAutocomplete extends Component {
  constructor(props) {
    super(props);

    const { initialValue, initialItems } = props;

    this.state = {
      customerID: window.merchantos.session.getSystemCustomerID(),
      value: initialValue === null ? '' : initialValue,
      options: initialItems,
      isLoading: false,
      isError: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleNewOptionClick = this.handleNewOptionClick.bind(this);
  }

  getOptions() {
    const { options } = this.state;
    const { hasNone } = this.props;

    return hasNone ? [{ value: 0, label: 'None', translate: true }].concat(options) : options;
  }

  async createBrand(label) {
    const { options, customerID } = this.state;
    try {
      this.setState({ isLoading: true });
      const response = await axios.post(`/API/Account/${customerID}/Manufacturer.json`, {
        name: label,
      });
      const { data: { Manufacturer: { manufacturerID, name } = {} } = {} } = response;
      this.setState(
        {
          options: [{ value: manufacturerID, label: name }]
            .concat(options)
            .sort((a, b) => sortCaseInsensitive(a.label, b.label)),
        },
        () => this.handleChange(manufacturerID)
      );
    } catch (e) {
      submitErrors({ detail: 'There was an error while adding the brand' });
      this.setState({ isError: true });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async handleNewOptionClick(newOption = {}) {
    await this.createBrand(newOption.label);
  }

  handleChange(value) {
    const { triggerViewFieldChanged, onChangeCallback } = this.props;

    this.setState({
      value: value === null ? '' : value,
    });

    if (triggerViewFieldChanged) {
      window.viewFieldChanged('view');
    }

    if (onChangeCallback) {
      runOnChangeCallbackFunc(onChangeCallback, value);
    }
  }

  handleInputChange(value) {
    if (this.state.isError) {
      this.setState({ isError: false });
    }
    return value;
  }

  render() {
    const { value, isLoading, isError } = this.state;
    const { name, placeholder, isCreatable, isClearable, inputProps, tabIndex } = this.props;

    return (
      <Autocomplete
        name={name}
        options={this.getOptions()}
        value={value}
        placeholder={placeholder}
        isLoading={isLoading}
        isError={isError}
        inputProps={inputProps}
        creatable={isCreatable}
        clearable={isClearable}
        tabIndex={tabIndex}
        onInputChange={this.handleInputChange}
        onChange={this.handleChange}
        onNewOptionClick={this.handleNewOptionClick}
      />
    );
  }
}

BrandsAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialItems: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  inputProps: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    maxLength: PropTypes.number,
  }),
  tabIndex: PropTypes.string,
  isCreatable: PropTypes.bool,
  isClearable: PropTypes.bool,
  hasNone: PropTypes.bool,
  triggerViewFieldChanged: PropTypes.bool,
  placeholder: PropTypes.string,
  onChangeCallback: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
};

BrandsAutocomplete.defaultProps = {
  initialValue: '',
  initialItems: [],
  inputProps: {},
  tabIndex: '0',
  isCreatable: true,
  isClearable: true,
  hasNone: false,
  triggerViewFieldChanged: true,
  placeholder: 'Select or Enter a Brand',
  onChangeCallback: '',
};

const RenderedComponent = createReactRenderer({
  component: BrandsAutocomplete,
  selector: '[data-react-root="BrandsAutocomplete"]',
});

history.onChangeState(
  [
    {
      name: [
        'item.views.item',
        'item.views.register_item',
        'item.views.quick_add',
        'item.views.register_workorder_item',
        'price_rule.views.price_rule',
        'purchase.views.purchase',
        'item_group.views.item_group',
        'transfer.views.transfer',
      ],
      id: undefined,
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
